/**
 * spiritualProcessDiscernment.js
 *
 * Identifies where a person appears to be in their spiritual process.
 * This shapes HOW Kevia responds — you don't give growth encouragement
 * to someone in quebranto, and you don't give grief support to someone
 * in crecimiento.
 *
 * States:
 *   resistencia          — resistance; closed, defensive, pushing back
 *   búsqueda             — seeking; open, questioning, spiritually curious
 *   quebranto            — brokenness; raw, tender, at the end of themselves
 *   restauración         — restoration; returning, reconnecting, healing
 *   crecimiento          — growth; stable, moving forward intentionally
 *   confusión            — confusion; disoriented spiritually but not collapsed
 *   agotamiento_espiritual — spiritual exhaustion; ministry fatigue, dry season
 *   apertura             — openness; receptive, ready to receive
 */

const STATE_INSTRUCTIONS = {
  resistencia:
    "La persona muestra resistencia. No presiones. Planta semillas con paciencia.",
  búsqueda:
    "Hay búsqueda espiritual activa. Acompaña las preguntas sin apresurarte a responderlas todas.",
  quebranto:
    "Momento de quebranto. Antes que palabras, presencia. Antes que dirección, contención.",
  restauración:
    "Proceso de restauración en marcha. Afirma el camino sin exigir velocidad.",
  crecimiento:
    "Hay crecimiento espiritual. Puedes celebrar y proponer próximos pasos con confianza.",
  confusión:
    "Confusión espiritual presente. Ofrece claridad sin rigidez. Ancla en lo esencial.",
  agotamiento_espiritual:
    "Señales de agotamiento ministerial o espiritual. Cuida antes de desafiar.",
  apertura:
    "Hay apertura y receptividad. Puedes ser más directo y propositivo.",
};

/**
 * @param {{
 *   text:    string,
 *   emotion: object|null,
 *   intent:  string,
 * }} params
 * @returns {{
 *   state:       string,
 *   instruction: string|null,
 * }}
 */
export function discernSpiritualProcess({ text, emotion, intent }) {
  const dom = emotion?.dominantEmotion ?? "none";
  const t   = text ?? "";

  // Quebranto — raw grief or crisis language
  if (
    dom === "grief" ||
    /no\s+puedo\s+más|ya\s+no\s+sé|estoy\s+destrozado|me\s+rompe/i.test(t)
  ) {
    return { state: "quebranto", instruction: STATE_INSTRUCTIONS.quebranto };
  }

  // Resistencia — pushback, defensive, "but"s
  if (
    /no\s+quiere|se\s+niega|resiste|no\s+me\s+escucha|está\s+cerrado|rechaza/i.test(t) ||
    /pero\s+(?:es\s+que|él|ella|ellos)\s+(?:no|nunca)/i.test(t)
  ) {
    return { state: "resistencia", instruction: STATE_INSTRUCTIONS.resistencia };
  }

  // Agotamiento espiritual — ministry exhaustion signals
  if (
    dom === "exhaustion" ||
    /agotado|quemado|ya\s+no\s+tengo\s+fuerzas|no\s+sé\s+si\s+puedo\s+seguir|temporada\s+seca/i.test(t)
  ) {
    return { state: "agotamiento_espiritual", instruction: STATE_INSTRUCTIONS.agotamiento_espiritual };
  }

  // Búsqueda — questions, seeking
  if (
    dom === "spiritual_confusion" ||
    /¿por\s+qué|¿qué\s+significa|estoy\s+buscando|no\s+entiendo|¿dios/i.test(t) ||
    intent === "reflection"
  ) {
    return { state: "búsqueda", instruction: STATE_INSTRUCTIONS.búsqueda };
  }

  // Confusión — disoriented but not broken
  if (
    /no\s+sé\s+qué\s+hacer|no\s+entiendo\s+por\s+qué|me\s+siento\s+perdido|confundido/i.test(t)
  ) {
    return { state: "confusión", instruction: STATE_INSTRUCTIONS.confusión };
  }

  // Restauración — returning, recovering
  if (
    /regresó|está\s+volviendo|quiere\s+volver|ha\s+dado\s+pasos|viene\s+de\s+nuevo/i.test(t)
  ) {
    return { state: "restauración", instruction: STATE_INSTRUCTIONS.restauración };
  }

  // Crecimiento — growth indicators
  if (
    /está\s+creciendo|ha\s+madurado|progresando|está\s+bien\s+espiritualmente|muy\s+comprometido/i.test(t) ||
    intent === "sermon"
  ) {
    return { state: "crecimiento", instruction: STATE_INSTRUCTIONS.crecimiento };
  }

  // Apertura — receptive/planning signals
  if (intent === "visit_plan" || intent === "family_followup" || intent === "prayer") {
    return { state: "apertura", instruction: null };
  }

  return { state: "apertura", instruction: null };
}
